import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { VOICE_LIMITS, type Conversation } from "@atrium/shared";
import { Avatar } from "../../components/ui/Avatar";
import { IconButton } from "../../components/ui/Button";
import { IconBellOff, IconBell, IconCall, IconEndCall } from "../../components/icons";
import { getSocket } from "../../lib/socket";
import { useMe } from "../auth/AuthProvider";
import { useCallStore, type IncomingCall } from "./stores";
import { useVoice } from "./VoiceProvider";

const RINGTONE_KEY = "atrium:voice:ringtone";

/** A soft two-note chime every few seconds, synthesised with WebAudio. Quiet by design. */
function useRingtone(active: boolean, enabled: boolean) {
  const ctx = useRef<AudioContext | null>(null);
  useEffect(() => {
    if (!active || !enabled) return;
    const play = () => {
      try {
        ctx.current ??= new AudioContext();
        const c = ctx.current;
        if (c.state === "suspended") void c.resume();
        const t0 = c.currentTime;
        const gain = c.createGain();
        gain.connect(c.destination);
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.05, t0 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.9);
        [659.25, 783.99].forEach((f, i) => {
          const o = c.createOscillator();
          o.type = "sine";
          o.frequency.setValueAtTime(f, t0 + i * 0.18);
          o.connect(gain);
          o.start(t0 + i * 0.18);
          o.stop(t0 + 0.95);
        });
      } catch {
        /* audio unavailable (e.g. no user gesture yet) */
      }
    };
    play();
    const t = setInterval(play, 3000);
    return () => clearInterval(t);
  }, [active, enabled]);
}

function useConversationTitle(conversationId: string, fallback: string) {
  const qc = useQueryClient();
  const me = useMe();
  const c = qc.getQueryData<Conversation[]>(["conversations"])?.find((x) => x.id === conversationId) ?? qc.getQueryData<Conversation>(["conversation", conversationId]);
  if (!c) return { title: fallback, group: false };
  const others = c.members.filter((m) => m.id !== me.id);
  return { title: c.name || others.map((m) => m.displayName).join(", ") || fallback, group: c.kind === "group" };
}

function IncomingCard({ call, silenced, onToggleSound }: { call: IncomingCall; silenced: boolean; onToggleSound: () => void }) {
  const voice = useVoice();
  const navigate = useNavigate();
  const { title, group } = useConversationTitle(call.conversationId, call.from.displayName);

  const accept = () => {
    navigate(`/messages/${call.conversationId}`);
    void voice.joinCall(call.conversationId, title);
  };
  const decline = () => {
    getSocket()?.emit("call:decline", { conversationId: call.conversationId });
    useCallStore.getState().stopRing(call.conversationId);
  };

  return (
    <div role="alertdialog" aria-label={`Incoming call from ${call.from.displayName}`} className="pointer-events-auto flex w-80 max-w-full items-center gap-3 rounded-xl bg-surface p-3 shadow-pop animate-pop-in">
      <span className="relative shrink-0">
        <Avatar src={call.from.avatarUrl} name={call.from.displayName} size="lg" />
        <span aria-hidden className="absolute -inset-1 rounded-full ring-2 ring-success motion-safe:animate-shimmer" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{title}</div>
        <div className="truncate text-xs text-fg-3">{group ? `${call.from.displayName} is calling the group` : "Incoming call"}</div>
      </div>
      <IconButton size="sm" label={silenced ? "Turn ringtone on" : "Silence ringtone"} icon={silenced ? IconBellOff : IconBell} onClick={onToggleSound} />
      <IconButton label="Decline" icon={IconEndCall} onClick={decline} className="bg-danger text-white hover:bg-danger hover:text-white hover:brightness-110" />
      <IconButton label="Accept" icon={IconCall} onClick={accept} className="bg-success text-white hover:bg-success hover:text-white hover:brightness-110" />
    </div>
  );
}

/** Ringing calls, shown wherever you are in the app. */
export function IncomingCalls() {
  const incoming = useCallStore((s) => s.incoming);
  const [silenced, setSilenced] = useState(() => localStorage.getItem(RINGTONE_KEY) === "off");
  useRingtone(incoming.length > 0, !silenced);

  // The server stops rings after a while; this is a backstop if that message is missed.
  useEffect(() => {
    if (!incoming.length) return;
    const t = setInterval(() => {
      const cutoff = Date.now() - (VOICE_LIMITS.ringSeconds + 5) * 1000;
      for (const i of useCallStore.getState().incoming) if (i.at < cutoff) useCallStore.getState().stopRing(i.conversationId);
    }, 2000);
    return () => clearInterval(t);
  }, [incoming.length]);

  if (!incoming.length) return null;
  const toggle = () =>
    setSilenced((v) => {
      localStorage.setItem(RINGTONE_KEY, v ? "on" : "off");
      return !v;
    });
  return (
    <div aria-live="assertive" className="pointer-events-none fixed inset-x-3 top-3 z-[90] flex flex-col items-center gap-2 md:inset-x-auto md:right-4 md:top-4 md:items-end">
      {incoming.map((c) => (
        <IncomingCard key={c.conversationId} call={c} silenced={silenced} onToggleSound={toggle} />
      ))}
    </div>
  );
}
